import { InputModalityDetector, InteractivityChecker } from '@angular/cdk/a11y';
import { booleanAttribute, Directive, effect, ElementRef, inject, input } from '@angular/core';

/**
 * Moves focus to the first tabbable element inside the host once it is ready — for async
 * dialog content where Material's open-time auto-focus would otherwise land on a footer
 * button (e.g. Cancel) because the real first field isn't focusable yet.
 *
 * Two ways to use it:
 *  - bare `cipoAutofocus` — focuses once the host renders. Use when the first field is
 *    gated behind a loading `@if`/`@else` and doesn't exist at open time.
 *  - `[cipoAutofocus]="ready"` — focuses when `ready` becomes true. Use when the first
 *    field is always rendered but disabled while loading, e.g. `[cipoAutofocus]="!loading()"`.
 *
 */
@Directive({
  selector: '[cipoAutofocus]',
  standalone: true,
})
export class CipoAutofocusDirective {
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly checker = inject(InteractivityChecker);
  private readonly modality = inject(InputModalityDetector);

  readonly ready = input(true, { alias: 'cipoAutofocus', transform: booleanAttribute });

  private scheduled = false;

  constructor() {
    effect(() => {
      if (this.scheduled || !this.ready()) {
        return;
      }
      this.scheduled = true;
      setTimeout(() => this.focusFirstTabbable());
    });
  }

  private focusFirstTabbable(): void {
    // Only steal focus for keyboard/screen-reader navigation; on mouse/touch leave focus
    // on the dialog container that Material already focused.
    if (this.modality.mostRecentModality !== 'keyboard') {
      return;
    }
    const host = this.host.nativeElement;
    const candidates: HTMLElement[] = [
      ...Array.from(host.querySelectorAll<HTMLElement>('input, select, textarea, button, a[href], [tabindex]')),
      host,
    ];
    for (const el of candidates) {
      if (this.checker.isTabbable(el)) {
        el.focus();
        return;
      }
    }
  }
}
